import { useState, useEffect, useRef, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { acquireApiToken } from "@/auth/acquireApiToken";
import { RecurrenceDay, DealStatus } from "./useDeals";

/**
 * Series-health rows for the recurring-deals monitor.
 *
 * Distinct from `useDeals`: that lists deals, this lists *series* with their
 * occurrence counts. `occurrences_upcoming` is the number that matters — the
 * generator only materializes 14 days ahead, so a series stops producing about
 * two weeks before its end date, and a low count is the early warning.
 */
export interface RecurringSeries {
  id: string;
  title: string;
  business_id: string;
  business_name: string;
  business_logo: string | null;
  business_timezone: string | null;
  status: DealStatus;
  recurrence_days: RecurrenceDay[] | null;
  recurrence_end_date: string | null;
  created_at: string;
  occurrences_total: number;
  occurrences_upcoming: number;
  next_occurrence: string | null;
  last_occurrence: string | null;
}

interface UseRecurringDealsResult {
  series: RecurringSeries[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecurringDeals(
  apiUrl?: string,
  enabled = true,
): UseRecurringDealsResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const [series, setSeries] = useState<RecurringSeries[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;

    const load = async () => {
      if (!apiUrl) {
        setError("API URL is not configured for this environment");
        return;
      }
      setLoading(true);
      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }
        const resp = await fetch(`${apiUrl}/admin/deals/recurring`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
        if (alive) {
          setSeries(data.items ?? []);
          setError(null);
        }
      } catch (err: any) {
        if (alive) {
          setError(err?.message ?? "Unknown error");
          setSeries([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    void load();
    return () => {
      alive = false;
    };
  }, [apiUrl, enabled, isAuthenticated, instance, tick]);

  return { series, loading, error, refetch };
}
